import React, { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Menu, X, Search as SearchIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { tools, ToolCategory } from '@/data/tools';
import { ToolIcon } from './tools/ToolIcon';

const categories: ToolCategory[] = [
    'Text',
    'Image',
    'Dev',
    'Network',
    'Converters',
    'Media',
    'Calculator',
    'Science',
    'SEO',
    'Utility',
    'PDF',
    'Design',
    'Brain Games',
    'Fun',
    'Discovery',
    'Security',
    'Earth & Space',
    'Explore'
];

export default function MobileMenu() {
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
    const [mounted, setMounted] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        setMounted(true);
    }, []);

    const filteredTools = useMemo(() => {
        if (!searchQuery.trim()) return tools;
        return tools.filter(tool =>
            tool.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            tool.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
            tool.description.toLowerCase().includes(searchQuery.toLowerCase())
        );
    }, [searchQuery]);

    const handleToolClick = (route: string) => {
        navigate(route);
        setIsOpen(false);
        setSearchQuery('');
        setExpandedCategory(null);
    };

    const toggleCategory = (category: string) => {
        setExpandedCategory(expandedCategory === category ? null : category);
    };

    return (
        <>
            {/* Hamburger Button */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="lg:hidden flex items-center justify-center p-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg transition-colors theme-transition"
                aria-label="Menu"
            >
                {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>

            {/* Mobile Drawer */}
            {mounted && isOpen && createPortal(
                <>
                    {/* Backdrop */}
                    <div
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] lg:hidden"
                        onClick={() => setIsOpen(false)}
                    />

                    {/* Drawer */}
                    <div className="fixed top-0 left-0 bottom-0 w-[280px] sm:w-80 bg-white dark:bg-slate-900 z-[101] shadow-2xl overflow-y-auto lg:hidden animate-in slide-in-from-left duration-300 theme-transition">
                        {/* Header */}
                        <div className="sticky top-0 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md border-b border-gray-200 dark:border-white/10 p-4 z-20">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-lg font-black text-gray-900 dark:text-white tracking-tight">VibeTools</h2>
                                <button
                                    onClick={() => setIsOpen(false)}
                                    className="p-2 text-gray-500 hover:text-gray-900 dark:hover:text-white rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
                                >
                                    <X size={20} />
                                </button>
                            </div>

                            {/* Search In Menu */}
                            <div className="relative">
                                <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                                <input
                                    type="text"
                                    placeholder="Search 330+ tools..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full bg-gray-50 dark:bg-slate-800 border border-gray-100 dark:border-white/10 rounded-xl py-2.5 pl-10 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 theme-transition text-gray-900 dark:text-white"
                                />
                            </div>
                        </div>

                        {/* Search Results or Categories */}
                        <div className="p-4">
                            {searchQuery.trim() ? (
                                <div className="space-y-1">
                                    <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-3 mb-2">Search Results ({filteredTools.length})</h3>
                                    {filteredTools.slice(0, 20).map((tool) => (
                                        <button
                                            key={tool.id}
                                            onClick={() => handleToolClick(tool.route)}
                                            className="w-full flex items-start gap-3 p-3 hover:bg-gray-50 dark:hover:bg-slate-800/50 rounded-xl transition-colors text-left group"
                                        >
                                            <div className="p-2 rounded-lg flex-shrink-0 theme-transition bg-blue-50 text-blue-600 dark:bg-blue-500/10 dark:text-blue-400">
                                                <ToolIcon icon={tool.icon} size={16} />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-sm font-bold text-gray-900 dark:text-gray-100 truncate">
                                                        {tool.name}
                                                    </span>
                                                </div>
                                                <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-1 italic">
                                                    {tool.category}
                                                </p>
                                            </div>
                                        </button>
                                    ))}
                                    {filteredTools.length > 20 && (
                                        <p className="text-center text-xs text-gray-400 py-4">Keep typing to refine search...</p>
                                    )}
                                    {filteredTools.length === 0 && (
                                        <div className="text-center py-12">
                                            <SearchIcon size={32} className="mx-auto text-gray-300 mb-3" />
                                            <p className="text-sm text-gray-500">No tools found</p>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                categories.map((category) => {
                                    const categoryTools = tools.filter((tool) => tool.category === category);
                                    if (categoryTools.length === 0) return null;

                                    const isExpanded = expandedCategory === category;

                                    return (
                                        <div key={category} className="mb-2">
                                            {/* Category Header */}
                                            <button
                                                onClick={() => toggleCategory(category)}
                                                className={`w-full flex items-center justify-between p-3.5 rounded-xl transition-all ${isExpanded
                                                    ? 'bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400'
                                                    : 'bg-gray-50 dark:bg-slate-800/50 text-gray-900 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-800'
                                                    }`}
                                            >
                                                <span className="font-bold text-sm tracking-tight">{category}</span>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-[10px] font-bold opacity-60">{categoryTools.length}</span>
                                                    <div className={`w-1.5 h-1.5 rounded-full transition-transform ${isExpanded ? 'bg-blue-500 scale-125' : 'bg-gray-300 dark:bg-gray-600'}`}></div>
                                                </div>
                                            </button>

                                            {/* Category Tools */}
                                            {isExpanded && (
                                                <div className="mt-2 ml-2 pl-2 border-l-2 border-gray-100 dark:border-white/5 space-y-1 animate-in slide-in-from-top-2 duration-200">
                                                    {categoryTools.map((tool) => (
                                                        <button
                                                            key={tool.id}
                                                            onClick={() => handleToolClick(tool.route)}
                                                            className="w-full flex items-center gap-3 p-2.5 hover:bg-gray-50 dark:hover:bg-slate-800/50 rounded-xl transition-colors text-left"
                                                        >
                                                            <div className="p-1.5 rounded-lg flex-shrink-0 text-blue-600 dark:text-blue-400">
                                                                <ToolIcon icon={tool.icon} size={14} />
                                                            </div>
                                                            <span className="text-[13px] font-semibold text-gray-800 dark:text-gray-200 truncate flex-1">
                                                                {tool.name}
                                                            </span>
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </>,
                document.body
            )}
        </>
    );
}
